import { DynamicEmailListProps } from '@type/bulk-operations';
import { ApiBulkSendPopupData } from '@type/v1-api-types/bulk-operations';
import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useSwrBulkSendPopupData } from '@hooks/useSwrApi/endpoints/bulk-operations';
import { Button, Input } from '@components/common/Forms';
import { Spinner } from '@components/common';
import { TrashIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';


// Default email object to be used in the form
// ********************************
const emailObject: ApiBulkSendPopupData = {
  defaultEmail: '',
};


/**
 * @description The dynamic email list component. It's a list of email inputs that can be added or removed.
 *
 * - ***record** - the record function from useBjForm.
 * - ***locale** - the locale. Defaults to 'ro'.
 * - ***updateFormElements** - function that updates the form elements.
 */
export const DynamicEmailList = (props: DynamicEmailListProps) => {
  const {
    record,
    locale = i18nConfig.defaultLocale,
    updateFormElements,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // State for the email list
  const [emailList, setEmailList] = useState<ApiBulkSendPopupData[] | undefined>(undefined);


  // SWR: Get email list
  const {
    data,
    isLoading,
  } = useSwrBulkSendPopupData(locale);


  // Add more emails
  const addMoreEmails = () => {
    setEmailList((prev) => {
      if (prev) return [...prev, emailObject];
      return [emailObject];
    });
  };


  // Delete email
  const deleteEmail = (index: number) => {
    setEmailList((prev) => {
      if (prev) {
        const temp = [...prev];
        temp.splice(index, 1);
        return temp;
      }
      return prev;
    });
  };


  // Effect: Set the email list
  useEffect(() => {
    if (data && data?.length > 0) {
      setEmailList(data);
    } else {
      setEmailList([emailObject]);
    }
  }, [data]);


  // Effect: Update the form elements on email list change
  useEffect(() => {
    updateFormElements();
  }, [emailList, updateFormElements]);


  // Render component
  // ********************************
  return (
    <>
      {/* Spinner */}
      {!emailList && isLoading && <div className="h-12"><Spinner /></div>}

      {/* Email list */
        emailList && emailList.map((email, index) => (
          <div
            key={email.defaultEmail || `emails_${index}`}
            className="mt-3 flex content-start items-start gap-3"
          >
            {/* Email input */}
            <div className="grow">
              <Input
                required
                type="email"
                className="asOptional"
                placeholder={t('add.email.placeholder')}
                {...record('emails', {
                  id: `emails_${index}`,
                  defaultValue: email.defaultEmail,
                  valueAsArray: true,
                })}
              />
            </div>

            {/* Remove email button */
              index > 0 && (
                <Button
                  size="sm"
                  styling="outline"
                  color="light"
                  className="shrink-0 !border-ink/15 !py-3.5 !text-ink-medium hover:!bg-error hover:!text-surface"
                  aria-label={t('global.action.delete')}
                  title={t('global.action.delete')}
                  onClick={() => deleteEmail(index)}
                >
                  <TrashIcon className="size-5" />
                </Button>
              )
            }
          </div>
        ))
      }

      {/* Add more emails */}
      <Button
        styling="text"
        className="mt-6 font-semibold"
        onClick={addMoreEmails}
      >
        <span className="mr-1">+</span>
        {t('bulk.send_email.collection_add.email')}
      </Button>
    </>
  );
};
